import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus } from "lucide-react";
import { FadeUp, Eyebrow } from "./Reveal";

const SERVICES = [
    { id: "roof-installation", name: "Roof Installation", desc: "New trusses, battens and precision-laid corrugated, IBR or Roman tile sheets — built for lifelong endurance." },
    { id: "roof-inspection", name: "Roof Inspection", desc: "Pre-purchase and rainy-season assessments for leaks, sag points, wood rot and water pooling — before disaster strikes." },
    { id: "roof-repair", name: "Roof Repair", desc: "Sealants, fastener replacement, re-sheeting and ridge realignment that restore waterproof integrity fast." },
    { id: "gutter-installation", name: "Gutter Installation", desc: "High-volume rainwater channels, bespoke downspouts and fascia brackets that protect your foundation from erosion." },
    { id: "gutter-repairs", name: "Gutter Repairs", desc: "Re-sloping sagging troughs, sealing joint separations and clearing heavy seasonal blockages." },
    { id: "roof-damage-repair", name: "Roof Damage Repair", desc: "Structural repair for damaged purlins, fallen-branch impact and degraded timber — reinforced the right way." },
    { id: "storm-wind-damage", name: "Roof Repair for Storm & Wind Damage", desc: "Rapid-response crew for torn sheets and lifted flashing after storms, dispatched across Sunyani and nearby areas." },
];

export const Services = ({ onBook }) => {
    const [openId, setOpenId] = useState(SERVICES[0].id);

    return (
        <section id="services" data-testid="services-numbered-section" className="relative py-24 sm:py-32">
            <div className="max-w-7xl mx-auto px-5 sm:px-8">
                <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-14">
                    <FadeUp>
                        <Eyebrow>What we do · 01—07</Eyebrow>
                        <h2 className="font-display font-bold uppercase text-4xl sm:text-6xl leading-[1.02] mt-5">
                            Seven services.<br />
                            <span className="text-outline">One standard.</span>
                        </h2>
                    </FadeUp>
                    <FadeUp delay={0.15} className="max-w-xs">
                        <p className="text-sm leading-relaxed text-[#94A3B8]">
                            From a single missing sheet to a full roof — every job gets
                            quality materials and the same careful hands.
                        </p>
                    </FadeUp>
                </div>

                <div className="border-b border-white/10">
                    {SERVICES.map((s, i) => {
                        const open = openId === s.id;
                        return (
                            <FadeUp key={s.id} delay={i * 0.05}>
                                <div className={`border-t border-white/10 transition-colors ${open ? "bg-white/[0.03]" : "hover:bg-white/[0.02]"}`}>
                                    <button
                                        data-testid={`service-item-${s.id}`}
                                        onClick={() => setOpenId(open ? null : s.id)}
                                        className="w-full flex items-center gap-5 sm:gap-8 text-left px-2 sm:px-4 py-6 sm:py-7"
                                    >
                                        <span className={`font-mono2 text-xs sm:text-sm transition-colors ${open ? "text-amber-500" : "text-[#64748B]"}`}>
                                            0{i + 1}
                                        </span>
                                        <span className={`font-display font-bold uppercase text-xl sm:text-3xl md:text-4xl flex-1 transition-colors ${open ? "text-amber-500" : "text-[#F8FAFC]"}`}>
                                            {s.name}
                                        </span>
                                        <motion.span animate={{ rotate: open ? 45 : 0 }} transition={{ duration: 0.3 }} className="text-[#F8FAFC]">
                                            <Plus size={22} strokeWidth={2.5} />
                                        </motion.span>
                                    </button>
                                    <AnimatePresence initial={false}>
                                        {open && (
                                            <motion.div
                                                initial={{ height: 0, opacity: 0 }}
                                                animate={{ height: "auto", opacity: 1 }}
                                                exit={{ height: 0, opacity: 0 }}
                                                transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                                                className="overflow-hidden"
                                            >
                                                <div className="px-2 sm:px-4 pb-7 sm:pl-[4.5rem] flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
                                                    <p className="text-sm sm:text-base text-[#94A3B8] max-w-xl leading-relaxed">{s.desc}</p>
                                                    <a
                                                        href="#contact"
                                                        data-testid={`service-book-${s.id}`}
                                                        onClick={() => onBook(s.name)}
                                                        className="shrink-0 font-mono2 text-[11px] uppercase tracking-[0.2em] text-amber-500 border-b border-amber-500/50 hover:border-amber-500 pb-1 transition-colors w-fit"
                                                    >
                                                        Book this service →
                                                    </a>
                                                </div>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </div>
                            </FadeUp>
                        );
                    })}
                </div>
            </div>
        </section>
    );
};

export default Services;
